import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  TrendingUp,
  FileText,
  Layers,
  Zap,
  Check,
  LineChart as LineChartIcon,
  Quote,
  Building2,
  Globe2,
  ScrollText,
  Cpu,
  ChevronRight } from
"lucide-react";
import { Button } from "@/components/ui/button";

/* ───────────────────────── Shared motion primitives ───────────────────────── */

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }
  })
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
};

/* ───────────────────────────────── Hero ───────────────────────────────── */

function Hero() {
  const reduce = useReducedMotion();
  return (
    <section className="relative overflow-hidden">
            {/* Editorial gradient wash */}
            <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
            <div className="absolute inset-0 bg-grid-subtle bg-grid-fade opacity-[0.55] pointer-events-none" />
            <div className="absolute top-24 -left-40 w-[520px] h-[520px] rounded-full bg-accent/12 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 -right-40 w-[520px] h-[520px] rounded-full bg-gold/15 blur-[120px] pointer-events-none" />

            <div className="relative container max-w-[1240px] mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-28">
                <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="max-w-[900px]">

                    <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-card border border-border-strong/60 shadow-sm">
                        <span className="relative inline-flex">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                            <span className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-accent animate-ping opacity-60" />
                        </span>
                        <span className="text-[12px] tracking-wide uppercase text-muted-foreground font-medium">
                            Bank Statement Analysis · Live in 5 markets
                        </span>
                    </motion.div>

                    <motion.h1
            variants={fadeUp}
            custom={1}
            data-testid="hero-headline"
            className="text-balance !text-[44px] !leading-[0.98] !tracking-[-0.035em] sm:!text-[60px] lg:!text-[78px] mt-7 text-foreground">

                        The financial intelligence{" "}
                        <span className="relative inline-block">
                            <span className="italic">layer</span>
                            <svg
                aria-hidden
                viewBox="0 0 220 16"
                className="absolute -bottom-2 left-0 w-full h-3 text-accent/80"
                preserveAspectRatio="none">

                                <path
                  d="M2 12 C 40 2, 100 2, 140 8 S 210 14, 218 6"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round" />

                            </svg>
                        </span>{" "}
                        for modern lenders.
                    </motion.h1>

                    <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-7 text-[17px] sm:text-[18.5px] leading-[1.6] text-muted-foreground max-w-[620px] text-pretty">

                        CredNX parses, enriches and underwrites financial documents from every
                        bank in Southeast Asia — turning messy PDFs into decisions in under
                        ninety seconds.
                    </motion.p>

                    <motion.div variants={fadeUp} custom={3} className="mt-9 flex flex-wrap items-center gap-3">
                        <Button variant="hero" size="xl" asChild data-testid="hero-cta-demo">
                            <Link to="/contact">
                                Request a private demo
                                <ArrowUpRight className="w-4 h-4 ml-1" />
                            </Link>
                        </Button>
                        <Button variant="outline-dark" size="xl" asChild data-testid="hero-cta-explore">
                            <Link to="/products/bsa">
                                Explore Bank Statement Analysis
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </Link>
                        </Button>
                    </motion.div>

                    <motion.div variants={fadeUp} custom={4} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-muted-foreground">
                        <div className="inline-flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-primary" />
                            SOC 2 Type II · ISO 27001
                        </div>
                        <div className="inline-flex items-center gap-2">
                            <Zap className="w-4 h-4 text-primary" />
                            Average parse time 41s
                        </div>
                        <div className="inline-flex items-center gap-2">
                            <Globe2 className="w-4 h-4 text-primary" />
                            220+ banks · 5 markets
                        </div>
                    </motion.div>
                </motion.div>

                {/* Floating product vignette */}
                <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-16 md:mt-20"
          data-testid="hero-product-card">

                    <div className="absolute -inset-6 bg-gradient-mesh-soft rounded-[32px] blur-2xl opacity-80 pointer-events-none" />
                    <div className="relative rounded-[24px] border border-border-strong/60 bg-card shadow-elegant overflow-hidden">
                        <div className="flex items-center justify-between border-b border-border px-5 py-3.5 bg-background-soft/60">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-destructive/70" />
                                <span className="w-2.5 h-2.5 rounded-full bg-warning/80" />
                                <span className="w-2.5 h-2.5 rounded-full bg-success/80" />
                                <span className="ml-3 text-[12px] text-muted-foreground font-mono">
                                    crednx.app / session / BSA-48291
                                </span>
                            </div>
                            <div className="hidden md:flex items-center gap-3 text-[11.5px] text-muted-foreground">
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-soft" />
                                    Session live
                                </span>
                                <span>·</span>
                                <span>Mandiri · 6-month statement</span>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-12 gap-0">
                            {/* Left column: summary */}
                            <div className="md:col-span-4 border-r border-border p-6 md:p-8 bg-gradient-to-b from-background-soft/70 to-transparent">
                                <p className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-medium">
                                    Borrower · PT Andalan
                                </p>
                                <p className="mt-3 font-serif text-[30px] leading-[1.05] tracking-tight">
                                    Cash-flow positive
                                </p>
                                <p className="mt-1.5 text-[13px] text-muted-foreground">
                                    Net-margin stable across 6 months with modest seasonality.
                                </p>

                                <dl className="mt-7 space-y-4">
                                    {[
                  { k: "Monthly inflow", v: "IDR 482.1M", trend: "+7.4%" },
                  { k: "Monthly outflow", v: "IDR 361.0M", trend: "+2.1%" },
                  { k: "Surplus ratio", v: "25.1%", trend: "+3.8%" },
                  { k: "Risk score", v: "82 / A-", trend: "stable" }].
                  map((r) =>
                  <div key={r.k} className="flex items-end justify-between border-b border-border/70 pb-3">
                                            <div>
                                                <dt className="text-[11.5px] text-muted-foreground uppercase tracking-wider">{r.k}</dt>
                                                <dd className="mt-1 text-[15.5px] font-medium text-foreground">{r.v}</dd>
                                            </div>
                                            <span className="text-[11.5px] text-success font-mono">{r.trend}</span>
                                        </div>
                  )}
                                </dl>
                            </div>

                            {/* Right column: chart + row */}
                            <div className="md:col-span-8 p-6 md:p-8">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-serif text-[22px] tracking-tight">Cash flow · last 180 days</p>
                                        <p className="text-[12.5px] text-muted-foreground mt-0.5">Inflow vs. outflow · category-coded</p>
                                    </div>
                                    <div className="hidden sm:flex items-center gap-3 text-[11.5px]">
                                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                            <span className="w-2 h-2 rounded-sm bg-primary" /> Inflow
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                            <span className="w-2 h-2 rounded-sm bg-accent" /> Outflow
                                        </span>
                                    </div>
                                </div>

                                <MiniChart reduce={reduce} />

                                {/* Transactions preview */}
                                <div className="mt-6 rounded-xl border border-border bg-background-soft/50 overflow-hidden">
                                    <div className="grid grid-cols-12 text-[11px] uppercase tracking-wider text-muted-foreground px-4 py-2.5 bg-muted/60 border-b border-border">
                                        <div className="col-span-2">Date</div>
                                        <div className="col-span-5">Description</div>
                                        <div className="col-span-2">Category</div>
                                        <div className="col-span-3 text-right">Amount</div>
                                    </div>
                                    {[
                  { d: "14 Feb", desc: "PT Sinar Utama · invoice", c: "Revenue", a: "+ 42,300,000", pos: true },
                  { d: "13 Feb", desc: "Payroll — 18 employees", c: "Opex", a: "− 88,450,000" },
                  { d: "12 Feb", desc: "GoBills · electricity", c: "Utilities", a: "− 3,120,000" },
                  { d: "11 Feb", desc: "PT Mitra Sejahtera · inflow", c: "Revenue", a: "+ 67,000,000", pos: true }].
                  map((t, i) =>
                  <div
                    key={i}
                    className={`grid grid-cols-12 px-4 py-3 text-[13px] ${i !== 3 ? "border-b border-border/70" : ""}`}>

                                            <div className="col-span-2 text-muted-foreground font-mono">{t.d}</div>
                                            <div className="col-span-5 text-foreground truncate">{t.desc}</div>
                                            <div className="col-span-2">
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/5 text-primary/90 border border-primary/10">
                                                    {t.c}
                                                </span>
                                            </div>
                                            <div className={`col-span-3 text-right font-mono ${t.pos ? "text-success" : "text-foreground/80"}`}>
                                                IDR {t.a}
                                            </div>
                                        </div>
                  )}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>);

}

function MiniChart({ reduce }) {
  // Playful, hand-drawn-feeling cash-flow chart — purely visual
  const inflow = [20, 42, 34, 58, 48, 72, 66, 84, 72, 90, 80, 96];
  const outflow = [18, 28, 30, 36, 42, 46, 50, 58, 54, 62, 60, 68];

  const W = 560;
  const H = 180;
  const max = 110;
  const step = W / (inflow.length - 1);

  const path = (arr) =>
  arr.
  map((v, i) => {
    const x = i * step;
    const y = H - v / max * H;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).
  join(" ");

  const area = (arr) =>
  `${path(arr)} L${W},${H} L0,${H} Z`;

  return (
    <div className="relative mt-5 h-[200px] w-full">
            <svg viewBox={`0 0 ${W} ${H + 20}`} preserveAspectRatio="none" className="w-full h-full">
                <defs>
                    <linearGradient id="g-in" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(155 45% 18%)" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="hsl(155 45% 18%)" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="g-out" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(74 70% 60%)" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="hsl(74 70% 60%)" stopOpacity="0" />
                    </linearGradient>
                </defs>

                {/* Grid */}
                {[0, 0.25, 0.5, 0.75, 1].map((t) =>
        <line
          key={t}
          x1={0}
          x2={W}
          y1={H * t}
          y2={H * t}
          stroke="hsl(155 12% 88%)"
          strokeDasharray="2 4"
          strokeWidth="1" />

        )}

                <path d={area(inflow)} fill="url(#g-in)" />
                <path d={area(outflow)} fill="url(#g-out)" />

                <motion.path
          d={path(inflow)}
          fill="none"
          stroke="hsl(155 45% 18%)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0 }}
          animate={reduce ? {} : { pathLength: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.7 }} />

                <motion.path
          d={path(outflow)}
          fill="none"
          stroke="hsl(74 70% 45%)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="1 5"
          initial={reduce ? false : { pathLength: 0 }}
          animate={reduce ? {} : { pathLength: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.9 }} />


                {/* Endpoint dot */}
                <circle cx={W} cy={H - inflow[inflow.length - 1] / max * H} r="4" fill="hsl(155 45% 18%)" />
                <circle cx={W} cy={H - inflow[inflow.length - 1] / max * H} r="8" fill="hsl(155 45% 18%)" opacity="0.15" />
            </svg>
        </div>);

}

/* ───────────────────────────────── Logos / Trust ───────────────────────────────── */

const TRUST_LOGOS = [
"Bank Mandiri",
"BCA",
"DBS",
"CIMB Niaga",
"Maybank",
"OCBC NISP",
"Kredivo",
"Akulaku",
"Investree",
"Amartha",
"Modalku",
"Permata"];


function Logos() {
  return (
    <section className="relative border-y border-border bg-background-soft/60 overflow-x-hidden">
            <div className="container max-w-[1240px] mx-auto px-6 py-10">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
                    <p className="text-[12px] tracking-[0.2em] uppercase text-muted-foreground font-medium shrink-0 md:border-r md:border-border md:pr-8">
                        Trusted by lenders<br className="hidden md:block" /> across Southeast Asia
                    </p>
                    <div className="relative flex-1 w-full min-w-0 overflow-hidden" style={{
            maskImage: "linear-gradient(90deg, transparent, black 12%, black 88%, transparent)",
            WebkitMaskImage: "linear-gradient(90deg, transparent, black 12%, black 88%, transparent)"
          }}>
                        <div className="flex gap-12 animate-marquee whitespace-nowrap">
                            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((name, i) =>
              <span
                key={i}
                className="font-serif text-[22px] md:text-[26px] tracking-tight text-foreground/55 hover:text-foreground transition-colors">

                                    {name}
                                </span>
              )}
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── Geo strip ───────────────────────────────── */

const MARKETS = [
{ code: "ID", name: "Indonesia", banks: 86, flag: "🇮🇩" },
{ code: "SG", name: "Singapore", banks: 22, flag: "🇸🇬" },
{ code: "MY", name: "Malaysia", banks: 38, flag: "🇲🇾" },
{ code: "PH", name: "Philippines", banks: 41, flag: "🇵🇭" },
{ code: "VN", name: "Vietnam", banks: 33, flag: "🇻🇳" }];


function GeoStrip() {
  return (
    <section className="relative">
            <div className="container max-w-[1240px] mx-auto px-6 py-20 md:py-28">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                    <div className="lg:col-span-7">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Coverage</p>
                        <h2 className="mt-4 font-serif text-[36px] md:text-[52px] leading-[1.02] tracking-tight text-foreground text-balance">
                            One parser. <span className="italic">Every bank</span>{" "}in the region.
                        </h2>
                    </div>
                    <div className="lg:col-span-5 text-[15.5px] text-muted-foreground max-w-[480px] lg:ml-auto">
                        From co-operative banks in Yogyakarta to digital-first incumbents in Singapore — every statement comes back in the same clean schema.
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
                    {MARKETS.map((m, i) =>
          <motion.div
            key={m.code}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="group relative rounded-2xl bg-card border border-border hover:border-primary/30 transition-all p-5 overflow-hidden">

                            <div className="absolute inset-0 bg-gradient-to-br from-accent/0 to-accent/0 group-hover:to-accent/10 transition-all" />
                            <div className="relative">
                                <div className="flex items-center justify-between">
                                    <span className="text-[26px] leading-none">{m.flag}</span>
                                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.code}</span>
                                </div>
                                <p className="mt-5 font-serif text-[22px] tracking-tight">{m.name}</p>
                                <p className="mt-1 text-[13px] text-muted-foreground">
                                    <span className="font-mono text-foreground">{m.banks}</span> banks live
                                </p>
                                <ChevronRight className="mt-4 w-4 h-4 text-muted-foreground/60 group-hover:text-primary group-hover:translate-x-0.5 transition-transform" />
                            </div>
                        </motion.div>
          )}
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── Product preview ───────────────────────────────── */

const PRODUCTS = [
{
  icon: Building2,
  title: "Bank Statement Analysis",
  blurb: "Standardise statements from 220+ banks across 5 markets. Transaction-level enrichment, cash-flow metrics, and fraud heuristics in a single API call.",
  live: true,
  to: "/products/bsa",
  meta: "220+ banks · 41s avg parse"
},
{
  icon: ScrollText,
  title: "Credit Bureau Analysis",
  blurb: "CIBIL, Experian, Pefindo parsing with normalised DPD, enquiry velocity and limit utilisation. Drop-in replacement for manual review.",
  live: false,
  to: "#",
  meta: "Private beta · Q2"
},
{
  icon: FileText,
  title: "Invoice Intelligence",
  blurb: "GST-grade extraction from B2B invoices — line items, taxes, counterparties — verified against registries.",
  live: false,
  to: "#",
  meta: "Waitlist open"
},
{
  icon: Layers,
  title: "Underwriting Workspace",
  blurb: "Orchestrate sessions, policies and decisions across product lines. Built for underwriters, not data scientists.",
  live: false,
  to: "#",
  meta: "Design partner program"
}];


function ProductPreview() {
  return (
    <section className="relative bg-surface-deep text-surface-deep-fg overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="absolute -top-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent/8 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-20 w-[480px] h-[480px] rounded-full bg-gold/8 blur-3xl pointer-events-none" />

            <div className="relative container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-accent/90 font-medium">The Product Suite</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[56px] leading-[1.02] tracking-tight text-balance">
                            Four products. <span className="italic text-surface-deep-fg/75">One ledger of truth.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-[15.5px] leading-relaxed text-surface-deep-fg/65 max-w-[480px] lg:ml-auto">
                        Built as independent APIs. Designed to compound when used together — a single borrower, rendered across every signal you need to decide.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                    {PRODUCTS.map((p, i) =>
          <motion.div
            key={p.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`product-card-${i}`}>

                            <Link
              to={p.to}
              className="group relative block h-full rounded-2xl border border-surface-deep-fg/10 bg-surface-deep-fg/[0.03] hover:bg-surface-deep-fg/[0.06] p-7 md:p-8 transition-all overflow-hidden">

                                <div className="flex items-start justify-between gap-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 rounded-xl bg-surface-deep-fg/10 inline-flex items-center justify-center border border-surface-deep-fg/10">
                                            <p.icon className="w-5 h-5 text-accent" />
                                        </div>
                                        <p className="text-[11.5px] tracking-[0.16em] uppercase text-surface-deep-fg/55">{p.meta}</p>
                                    </div>
                                    {p.live ?
                <span className="text-[10.5px] tracking-[0.14em] uppercase font-semibold px-2 py-1 rounded-full bg-accent/20 text-accent border border-accent/30">
                                            Live
                                        </span> :

                <span className="text-[10.5px] tracking-[0.14em] uppercase font-medium px-2 py-1 rounded-full bg-surface-deep-fg/5 text-surface-deep-fg/50 border border-surface-deep-fg/10">
                                            Soon
                                        </span>
                }
                                </div>

                                <h3 className="mt-7 font-serif text-[26px] md:text-[30px] tracking-tight leading-tight">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-[14.5px] leading-relaxed text-surface-deep-fg/65 max-w-[440px]">
                                    {p.blurb}
                                </p>

                                <div className="mt-8 inline-flex items-center gap-1.5 text-[13.5px] text-accent group-hover:gap-2.5 transition-all">
                                    {p.live ? "Open product" : "Join waitlist"}
                                    <ArrowUpRight className="w-4 h-4" />
                                </div>

                                <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-accent/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            </Link>
                        </motion.div>
          )}
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── How it works ───────────────────────────────── */

const STEPS = [
{
  n: "01",
  icon: FileText,
  title: "Ingest anything",
  blurb: "PDF, image, CSV — scanned, password-protected, stitched together. CredNX unlocks, OCRs and classifies inside your borrower session."
},
{
  n: "02",
  icon: Cpu,
  title: "Enrich with signal",
  blurb: "Each line is categorised, counterparties resolved, recurring patterns detected. No tuning required — we ship bank-specific models."
},
{
  n: "03",
  icon: ShieldCheck,
  title: "Decide with confidence",
  blurb: "Cash-flow metrics, stability ratios and fraud heuristics are delivered as one clean object — ready for your policy engine."
}];


function HowItWorks() {
  return (
    <section className="relative">
            <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="max-w-[760px] mb-16">
                    <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">How it works</p>
                    <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                        From a scanned PDF to a{" "}
                        <span className="italic">decision</span>
                        {" "}in under ninety seconds.
                    </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-0 border-t border-border">
                    {STEPS.map((s, i) =>
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className={`relative py-10 md:py-14 ${i !== STEPS.length - 1 ? "md:border-r border-border" : ""} ${i !== 0 ? "border-t md:border-t-0 border-border" : ""}`}>

                            <div className="px-6 md:px-8">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-[13px] text-muted-foreground">{s.n}</span>
                                    <s.icon className="w-5 h-5 text-primary/70" />
                                </div>
                                <h3 className="mt-8 font-serif text-[26px] tracking-tight leading-tight">{s.title}</h3>
                                <p className="mt-3 text-[14.5px] leading-relaxed text-muted-foreground max-w-[340px]">
                                    {s.blurb}
                                </p>
                            </div>
                        </motion.div>
          )}
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── Stats ───────────────────────────────── */

const STATS = [
{ n: "41s", l: "Average parse time", d: "Across 6-month SEA statements" },
{ n: "99.3%", l: "Field-level accuracy", d: "Benchmarked vs. human QA" },
{ n: "220+", l: "Banks supported", d: "Adding 6–8 per quarter" },
{ n: "4.1M", l: "Statements processed", d: "Since platform launch" }];


function Stats() {
  return (
    <section className="relative bg-background-soft/70 border-y border-border">
            <div className="absolute inset-0 bg-gradient-mesh-soft opacity-40 pointer-events-none" />
            <div className="relative container max-w-[1240px] mx-auto px-6 py-20 md:py-24">
                <div className="grid md:grid-cols-4 gap-8 md:gap-4">
                    {STATS.map((s, i) =>
          <motion.div
            key={s.l}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}>

                            <p className="font-serif text-[56px] md:text-[68px] leading-none tracking-[-0.04em] text-foreground">
                                {s.n}
                            </p>
                            <p className="mt-3 text-[14px] font-medium text-foreground">{s.l}</p>
                            <p className="mt-0.5 text-[13px] text-muted-foreground">{s.d}</p>
                        </motion.div>
          )}
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── Testimonial ───────────────────────────────── */

function Testimonial() {
  return (
    <section className="relative">
            <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-2">
                        <Quote className="w-10 h-10 text-accent" strokeWidth={1.5} />
                    </div>
                    <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-10">

                        <blockquote className="font-serif text-[28px] md:text-[40px] leading-[1.15] tracking-[-0.02em] text-balance">
                            <span className="text-foreground">
                                &ldquo;We replaced three manual review teams with one CredNX
                                workflow. Decisions that took 48 hours now run in a minute —
                                and our delinquency dropped <span className="italic">twelve points.</span>&rdquo;
                            </span>
                        </blockquote>
                        <figcaption className="mt-8 flex items-center gap-4">
                            <div className="w-11 h-11 rounded-full bg-gradient-gold flex items-center justify-center text-[13px] font-serif text-gold-foreground font-semibold">
                                AR
                            </div>
                            <div>
                                <p className="text-[14px] font-medium text-foreground">Anindya Ramadhan</p>
                                <p className="text-[12.5px] text-muted-foreground">Chief Risk Officer · Kredivo</p>
                            </div>
                        </figcaption>
                    </motion.figure>
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── Case studies ───────────────────────────────── */

const CASES = [
{
  tag: "Digital lender",
  name: "Kredivo",
  outcome: "−12pts delinquency",
  note: "Rebuilt underwriting on BSA + Bureau in 9 weeks.",
  color: "from-primary/90 to-primary-glow"
},
{
  tag: "Marketplace",
  name: "Investree",
  outcome: "3.1× throughput",
  note: "Moved SME onboarding from 48h to 22 minutes.",
  color: "from-primary-glow to-primary/70"
},
{
  tag: "Neobank",
  name: "Amartha",
  outcome: "99.2% parse rate",
  note: "Standardised 14 bank formats for rural borrowers.",
  color: "from-primary/80 to-primary-deep"
}];


function CaseStudies() {
  return (
    <section className="relative bg-background-soft/50 border-t border-border">
            <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-14">
                    <div className="max-w-[620px]">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Selected work</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                            Moved numbers in production.
                        </h2>
                    </div>
                    <Link to="/contact" className="inline-flex items-center gap-1.5 text-[14px] font-medium text-primary link-underline">
                        View all case studies <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="grid md:grid-cols-3 gap-5">
                    {CASES.map((c, i) =>
          <motion.a
            key={c.name}
            href="#"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="group relative rounded-2xl overflow-hidden border border-border bg-card hover:-translate-y-1 transition-transform duration-500"
            data-testid={`case-study-${i}`}>

                            <div className={`h-40 bg-gradient-to-br ${c.color} relative overflow-hidden`}>
                                <div className="absolute inset-0 grain opacity-30" />
                                <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between text-primary-foreground">
                                    <div>
                                        <p className="text-[10.5px] tracking-[0.16em] uppercase opacity-65">{c.tag}</p>
                                        <p className="font-serif text-[26px] tracking-tight mt-1">{c.name}</p>
                                    </div>
                                    <TrendingUp className="w-5 h-5 text-accent" />
                                </div>
                            </div>
                            <div className="p-6">
                                <p className="font-serif text-[32px] leading-none tracking-[-0.03em] text-foreground">{c.outcome}</p>
                                <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">{c.note}</p>
                                <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] text-primary font-medium group-hover:gap-2.5 transition-all">
                                    Read the story <ArrowUpRight className="w-3.5 h-3.5" />
                                </div>
                            </div>
                        </motion.a>
          )}
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── Final CTA ───────────────────────────────── */

function FinalCTA() {
  return (
    <section className="relative">
            <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="relative rounded-[28px] overflow-hidden border border-border-strong/60 bg-gradient-deep text-surface-deep-fg">
                    <div className="absolute inset-0 grain opacity-40" />
                    <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-accent/15 blur-3xl" />
                    <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-gold/15 blur-3xl" />

                    <div className="relative grid lg:grid-cols-12 gap-10 items-center p-10 md:p-16 lg:p-20">
                        <div className="lg:col-span-7">
                            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-accent">
                                <Sparkles className="w-3.5 h-3.5" /> Private demo
                            </div>
                            <h2 className="mt-5 font-serif text-[40px] md:text-[60px] leading-[1.02] tracking-tight text-balance">
                                See your own statements{" "}
                                <span className="italic text-surface-deep-fg/75">decoded</span>{" "}
                                in 30 minutes.
                            </h2>
                            <p className="mt-5 text-[15.5px] leading-relaxed text-surface-deep-fg/65 max-w-[520px]">
                                Bring five live statements. We&apos;ll parse them on-screen and walk your risk team through the output — signed NDA, no sales calls.
                            </p>
                        </div>

                        <div className="lg:col-span-5 space-y-3">
                            <Button variant="lime" size="xl" asChild className="w-full" data-testid="cta-final-demo">
                                <Link to="/contact">
                                    Book a private demo
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </Button>
                            <Button variant="outline" size="xl" asChild className="w-full border-surface-deep-fg/20 text-surface-deep-fg hover:bg-surface-deep-fg/10 hover:text-surface-deep-fg">
                                <Link to="/pricing">
                                    See transparent pricing
                                </Link>
                            </Button>

                            <div className="mt-6 pt-6 border-t border-surface-deep-fg/10 grid grid-cols-3 gap-4 text-[12px] text-surface-deep-fg/60">
                                {[
                { k: "No credit card", v: <Check className="w-3.5 h-3.5 text-accent" /> },
                { k: "Signed NDA", v: <Check className="w-3.5 h-3.5 text-accent" /> },
                { k: "30-min call", v: <LineChartIcon className="w-3.5 h-3.5 text-accent" /> }].
                map((c) =>
                <div key={c.k} className="inline-flex items-center gap-2">
                                        {c.v} {c.k}
                                    </div>
                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}

/* ───────────────────────────────── Page ───────────────────────────────── */

export default function Landing() {
  return (
    <div data-testid="page-landing">
            <Hero />
            <Logos />
            <GeoStrip />
            <ProductPreview />
            <HowItWorks />
            <Stats />
            <Testimonial />
            <CaseStudies />
            <FinalCTA />
        </div>);

}